import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/src/components/ui/alert-dialog'
import { Button } from '@/src/components/ui/button'
import { Badge } from '@/src/components/ui/badge'
import { Textarea } from '@/src/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select'
import {
  FolderIcon,
  UsersIcon,
  FileIcon,
  LockIcon,
  LockSimpleOpenIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  UserIcon,
  CalendarIcon,
  GlobeIcon,
} from '@phosphor-icons/react'
import type { FileDuplicateInfo, FileInProject } from '@/src/api/file-duplicate-check'
import { requestProjectAccess, contactProjectOwner } from '@/src/api/file-duplicate-check'
import { claimProject } from '@/src/api/project'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation, Trans } from '@/src/i18n/client'
import { DateTimeText } from '@/src/components/ui/date-time'

import { AVAILABLE_LANGUAGES } from '@/src/lib/availableLanguages'

const EMAIL_LANGUAGE_OPTIONS = AVAILABLE_LANGUAGES.filter(l => ['en','de','fr','es'].includes(l.code))

interface FileDuplicateWarningProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  duplicateInfo: FileDuplicateInfo
  onContinue: () => void
  fileName: string
}

export function FileDuplicateWarning({
  open,
  onOpenChange,
  duplicateInfo,
  onContinue,
  fileName,
}: FileDuplicateWarningProps) {
  const [sendingMessage, setSendingMessage] = useState<string | null>(null)
  const [accessMessage, setAccessMessage] = useState('')
  const [showMessageFor, setShowMessageFor] = useState<string | null>(null)
  const [messageMode, setMessageMode] = useState<'access' | 'contact'>('contact')
  const [claimedProjects, setClaimedProjects] = useState<Set<string>>(new Set())
  const [requestedProjects, setRequestedProjects] = useState<Set<string>>(new Set())
  const [contactedProjects, setContactedProjects] = useState<Set<string>>(new Set())
  const { i18n, t } = useTranslation()
  const [emailLocale, setEmailLocale] = useState(i18n.language?.split('-')[0] || 'en')

  if (!duplicateInfo.exists || !duplicateInfo.file) {
    return null
  }

  const { file, projects = [], uploadedBy = [] } = duplicateInfo

  const handleSelfClaim = async (project: FileInProject) => {
    try {
      await claimProject({ data: { id: project.id } })
      setClaimedProjects(prev => new Set(prev).add(project.id))
      toast.success(t('manage:fileDuplicateWarning.toast.claimSuccess', { name: project.name }))
    } catch (error) {
      toast.error(t('manage:fileDuplicateWarning.toast.claimError', { message: (error as Error).message }))
    }
  }

  const handleRequestAccess = async (project: FileInProject) => {
    setSendingMessage(project.id)
    try {
      await requestProjectAccess({
        data: {
          projectId: project.id,
          fileId: file.id,
          message: accessMessage || undefined,
          locale: emailLocale,
        },
      })
      setRequestedProjects(prev => new Set(prev).add(project.id))
      closeMessageForm()
      toast.success(t('manage:fileDuplicateWarning.toast.requestSent', { name: project.owner.name }))
    } catch (error) {
      toast.error(t('manage:fileDuplicateWarning.toast.requestError', { message: (error as Error).message }))
    } finally {
      setSendingMessage(null)
    }
  }

  const handleContactOwner = async (project: FileInProject) => {
    if (!accessMessage.trim()) {
      toast.error(t('manage:fileDuplicateWarning.toast.emptyMessage'))
      return
    }
    setSendingMessage(project.id)
    try {
      await contactProjectOwner({
        data: {
          projectId: project.id,
          message: accessMessage,
          locale: emailLocale,
        },
      })
      setContactedProjects(prev => new Set(prev).add(project.id))
      closeMessageForm()
      toast.success(t('manage:fileDuplicateWarning.toast.messageSent', { name: project.owner.name }))
    } catch (error) {
      toast.error(t('manage:fileDuplicateWarning.toast.messageError', { message: (error as Error).message }))
    } finally {
      setSendingMessage(null)
    }
  }

  const openMessageForm = (projectId: string, mode: 'access' | 'contact') => {
    setShowMessageFor(projectId)
    setMessageMode(mode)
    setAccessMessage('')
  }

  const closeMessageForm = () => {
    setShowMessageFor(null)
    setAccessMessage('')
  }

  const privacyIcon = (level: string) => {
    switch (level) {
      case 'LOW':
        return <LockSimpleOpenIcon className="w-3.5 h-3.5 text-green-600" weight="bold" />
      case 'MEDIUM':
        return <ShieldCheckIcon className="w-3.5 h-3.5 text-amber-600" weight="bold" />
      case 'HIGH':
        return <LockIcon className="w-3.5 h-3.5 text-red-600" weight="bold" />
      default:
        return null
    }
  }

  // Determine the most restrictive privacy across all uploaders
  const effectivePrivacy = uploadedBy.reduce((max, u) => {
    const order = { LOW: 0, MEDIUM: 1, HIGH: 2 } as Record<string, number>
    return (order[u.privacy] ?? 0) > (order[max] ?? 0) ? u.privacy : max
  }, 'LOW' as string)

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <FileIcon className="w-5 h-5 text-amber-500" weight="fill" />
            {t('manage:fileDuplicateWarning.title')}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base">
            {projects.length > 0 && effectivePrivacy !== 'HIGH'
              ? t('manage:fileDuplicateWarning.descriptionWithProjects')
              : t('manage:fileDuplicateWarning.descriptionUploadedOnly')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* File Info */}
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground">{t('manage:fileDuplicateWarning.originalUploadLabel')}</p>
                  <p className="font-medium truncate">{file.name}</p>
                </div>
                <Badge variant="secondary" className="ml-2 shrink-0">
                  <Link
                    to="/manage/files/$shareId"
                    params={{ shareId: file.shareId }}
                    className="hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    {t('manage:fileDuplicateWarning.viewFileLink')}
                  </Link>
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('manage:fileDuplicateWarning.uploadedOn', { date: new Date(file.createdAt).toISOString() })}
              </p>
            </div>
          </div>

          {/* Uploaded By — shown only when file is NOT in any (visible) projects */}
          {(projects.length === 0 || effectivePrivacy === 'HIGH') && uploadedBy.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <UsersIcon className="w-4 h-4" />
                <span>{t('manage:fileDuplicateWarning.uploadedByHeading')}</span>
              </div>
              <div className="space-y-1.5">
                {uploadedBy.slice(0, 5).map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between bg-muted/30 rounded px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 shrink-0">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarIcon className="w-3 h-3" />
                        <DateTimeText>{String(user.uploadedAt)}</DateTimeText>
                      </div>
                      <div className="flex items-center gap-1" title={t('manage:fileDuplicateWarning.privacyLabel', { privacy: user.privacy })}>
                        {privacyIcon(user.privacy)}
                      </div>
                    </div>
                  </div>
                ))}
                {uploadedBy.length > 5 && (
                  <p className="text-xs text-muted-foreground px-3">
                    {t('manage:fileDuplicateWarning.moreUsers', { count: uploadedBy.length - 5 })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Projects Using This File - privacy-aware */}
          {effectivePrivacy !== 'HIGH' && projects.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FolderIcon className="w-4 h-4" />
                <span>{t('manage:fileDuplicateWarning.usedInProjects', { count: projects.length })}</span>
              </div>
              <div className="space-y-2">
                {projects.map((project) => {
                  const isClaimed = claimedProjects.has(project.id) || project.isMember
                  const isRequested = requestedProjects.has(project.id)
                  const isContacted = contactedProjects.has(project.id)
                  const isSending = sendingMessage === project.id

                  return (
                    <div
                      key={project.id}
                      className="rounded-lg border bg-muted/30 p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{project.name}</p>
                            {privacyIcon(project.privacy)}
                          </div>
                          {project.description && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {project.description}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('manage:fileDuplicateWarning.projectByOwner', { name: project.owner.name, count: project.drawingCount })}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 ml-2 shrink-0">
                          {/* Access status / claim actions */}
                          {isClaimed ? (
                            <>
                              <div className="flex items-center gap-1 text-xs text-green-600">
                                <CheckCircleIcon className="w-4 h-4" weight="fill" />
                                <span>{t('manage:fileDuplicateWarning.memberBadge')}</span>
                              </div>
                              <Link
                                to="/manage/projects/$id"
                                params={{ id: project.id }}
                                search={{ edit: false }}
                                className="text-xs text-primary hover:underline"
                                onClick={() => onOpenChange(false)}
                              >
                                {t('manage:fileDuplicateWarning.viewLink')}
                              </Link>
                            </>
                          ) : project.privacy === 'LOW' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSelfClaim(project)}
                              className="text-xs h-7"
                            >
                              {t('manage:fileDuplicateWarning.claimButton')}
                            </Button>
                          ) : project.privacy === 'MEDIUM' ? (
                            isRequested ? (
                              <div className="flex items-center gap-1 text-xs text-amber-600">
                                <EnvelopeIcon className="w-4 h-4" />
                                <span>{t('manage:fileDuplicateWarning.requestedStatus')}</span>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMessageForm(project.id, 'access')}
                                disabled={isSending}
                                className="text-xs h-7"
                              >
                                <EnvelopeIcon className="w-3.5 h-3.5 mr-1" />
                                {t('manage:fileDuplicateWarning.requestAccessButton')}
                              </Button>
                            )
                          ) : null}

                          {/* Contact button — always available for non-owner projects */}
                          {!isContacted ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openMessageForm(
                                project.id,
                                (isClaimed || project.privacy === 'LOW') ? 'contact' : 'access'
                              )}
                              disabled={isSending}
                              className="text-xs h-7 px-2"
                              title={t('manage:fileDuplicateWarning.sendMessageTooltip', { name: project.owner.name })}
                            >
                              <EnvelopeIcon className="w-3.5 h-3.5" />
                            </Button>
                          ) : (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground" title={t('manage:fileDuplicateWarning.messageSentTooltip')}>
                              <CheckCircleIcon className="w-3.5 h-3.5 text-green-600" weight="fill" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Message form — for both contact and access requests */}
                      {showMessageFor === project.id && (
                        <div className="border-t pt-2 space-y-2">
                          <p className="text-xs text-muted-foreground">
                            <Trans 
                              i18nKey={messageMode === 'access'
                                ? 'manage:fileDuplicateWarning.accessRequestPrompt'
                                : 'manage:fileDuplicateWarning.contactPrompt'}
                              values={{ name: project.owner.name, email: project.owner.email }}
                            />
                          </p>
                          <Textarea
                            placeholder={messageMode === 'access' 
                              ? t('manage:fileDuplicateWarning.accessMessagePlaceholder')
                              : t('manage:fileDuplicateWarning.contactMessagePlaceholder')}
                            value={accessMessage}
                            onChange={(e) => setAccessMessage(e.target.value)}
                            className="text-xs min-h-15"
                            rows={2}
                          />
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5">
                              <GlobeIcon className="w-3.5 h-3.5 text-muted-foreground" />
                              <Select value={emailLocale} onValueChange={(val) => val && setEmailLocale(val)}>
                                <SelectTrigger className="h-7 text-xs w-[110px]">
                                  {EMAIL_LANGUAGE_OPTIONS.find(opt => opt.code === emailLocale)?.nameKey ? t(EMAIL_LANGUAGE_OPTIONS.find(opt => opt.code === emailLocale)!.nameKey) : emailLocale}
                                </SelectTrigger>
                                <SelectContent>
                                  {EMAIL_LANGUAGE_OPTIONS.map(opt => (
                                    <SelectItem key={opt.code} value={opt.code}>
                                      {t(opt.nameKey)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={closeMessageForm}
                              className="text-xs h-7"
                            >
                              {t('manage:fileDuplicateWarning.cancelButton')}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => messageMode === 'access' 
                                ? handleRequestAccess(project) 
                                : handleContactOwner(project)}
                              disabled={isSending || (messageMode === 'contact' && !accessMessage.trim())}
                              className="text-xs h-7"
                            >
                              {isSending 
                                ? t('manage:fileDuplicateWarning.sendingButton')
                                : messageMode === 'access' 
                                  ? t('manage:fileDuplicateWarning.sendRequestButton')
                                  : t('manage:fileDuplicateWarning.sendMessageButton')}
                            </Button>
                          </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {effectivePrivacy === 'HIGH' && projects.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 rounded px-3 py-2">
              <LockIcon className="w-4 h-4 text-red-500 shrink-0" />
              <span>{t('manage:fileDuplicateWarning.privacyHiddenMessage')}</span>
            </div>
          )}

          {/* Info Box */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950 p-3">
            <p className="text-sm text-amber-900 dark:text-amber-100">
              <strong>{t('manage:fileDuplicateWarning.noteTitle')}</strong> {t('manage:fileDuplicateWarning.noteMessage')}
            </p>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('manage:fileDuplicateWarning.cancelUploadButton')}</AlertDialogCancel>
          <Button onClick={() => {
            onContinue()
            onOpenChange(false)
          }}>
            {t('manage:fileDuplicateWarning.continueButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
